<template>
    <form @submit.prevent="saveAvailability" class="avail-form">

        <!-- ═══ Status Banner ═══ -->
        <div class="avail-banner" :class="availability.type === 'available' ? 'avail-banner--green' : 'avail-banner--red'">
            <i class="material-icons avail-banner__icon">{{ availability.type === 'available' ? 'check_circle' : 'cancel' }}</i>
            <span class="avail-banner__text">
                {{ availability.type === 'available' ? 'Setting times when you ARE available' : 'Setting times when you are NOT available' }}
            </span>
        </div>

        <!-- ═══ Step 1: Status ═══ -->
        <div class="avail-section">
            <div class="avail-section__header">
                <span class="avail-step-badge">1</span>
                <div>
                    <h6 class="avail-section__title">Set Your Status</h6>
                    <p class="avail-section__hint">Are you marking yourself as available or unavailable?</p>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div :class="availabilityTypeClass">
                        <MaterialSelect id="availabilityType" label="I am" :options="availabilityOptions"
                            v-model="availability.type" />
                    </div>
                </div>
                <div class="col-md-6 mb-3">
                    <MaterialSelect id="dateRange" label="Date Type" :options="dateRangeOptions"
                        v-model="availability.dateRange" />
                </div>
            </div>
        </div>

        <!-- ═══ Step 2: When ═══ -->
        <div class="avail-section">
            <div class="avail-section__header">
                <span class="avail-step-badge">2</span>
                <div>
                    <h6 class="avail-section__title">Choose When</h6>
                    <p class="avail-section__hint">
                        {{ availability.dateRange === 'recurring'
                            ? 'Set up a repeating pattern for your availability.'
                            : 'Pick the specific start and end dates.' }}
                    </p>
                </div>
            </div>

            <!-- Recurring options -->
            <div v-if="availability.dateRange === 'recurring'">
                <div class="row d-flex align-items-end">
                    <div class="col-md-6 mb-3">
                        <material-input id="repeatEvery" type="number" label="Repeat Every"
                            v-model="availability.repeatEvery" />
                    </div>
                    <div class="col-md-6 mb-3">
                        <MaterialSelect id="repeatEveryType" label="Period" :options="repeatEveryTypeOptions"
                            v-model="availability.repeatEveryType" />
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label fw-semibold mb-2">
                        <i class="material-icons text-sm align-middle me-1">today</i> Days of the Week
                    </label>
                    <div class="avail-day-grid">
                        <button v-for="day in weekDays" :key="day.value" type="button"
                            class="avail-day-chip" :class="{ 'avail-day-chip--active': availability.days.includes(day.value) }"
                            @click="toggleDay(day.value)">
                            {{ day.label }}
                        </button>
                    </div>
                </div>

                <!-- Advanced toggle -->
                <div class="avail-advanced-toggle" @click="showAdvancedOption = !showAdvancedOption">
                    <i class="material-icons text-sm">{{ showAdvancedOption ? 'expand_less' : 'expand_more' }}</i>
                    <span>{{ showAdvancedOption ? 'Hide' : 'Show' }} Advanced Options</span>
                </div>

                <div v-if="showAdvancedOption" class="avail-advanced-panel">
                    <div class="mb-3">
                        <label class="form-label fw-semibold mb-2">Week of Month:</label>
                        <div class="d-flex flex-wrap gap-2">
                            <button v-for="week in weekOnMonth" :key="week.value" type="button"
                                class="avail-day-chip avail-day-chip--sm"
                                :class="{ 'avail-day-chip--active': availability.weekOfMonth.includes(week.value) }"
                                @click="toggleWeeksOfMonth(week.value)">
                                {{ week.label }}
                            </button>
                        </div>
                    </div>

                    <div class="mb-3">
                        <MaterialMultiSelect v-model="availability.selectedDays" v-model:textValue="note"
                            :items="daysOfMonth" label="On Days of Month" placeholder="Select Dates..." itemTrackBy="value"
                            itemLabel="label" emitAs="object" :showTextField="true" />
                    </div>

                    <div class="mb-3">
                        <label class="form-label fw-semibold mb-2">On Week of Month:</label>
                        <div class="d-flex flex-wrap gap-2">
                            <button v-for="week in weekOfMonth2" :key="week.value" type="button"
                                class="avail-day-chip avail-day-chip--sm"
                                :class="{ 'avail-day-chip--active': availability.weeks.includes(week.value) }"
                                @click="toggleWeeks(week.value)">
                                {{ week.label }}
                            </button>
                        </div>
                    </div>

                    <div class="mb-3">
                        <MaterialMultiSelect v-model="availability.weeksOfYear" v-model:textValue="note"
                            :items="weeksOfYear()" label="Week of Year" placeholder="Select Week..." itemTrackBy="value"
                            itemLabel="label" emitAs="object" :showTextField="true" />
                    </div>

                    <div class="mb-3">
                        <MaterialMultiSelect v-model="availability.months" v-model:textValue="note" :items="months"
                            label="Month" placeholder="Select Month..." itemTrackBy="value" itemLabel="label"
                            emitAs="object" :showTextField="true" />
                    </div>
                </div>

                <!-- Recurring date range -->
                <div class="row mt-2">
                    <div class="col-md-6 mb-3">
                        <MaterialDatePicker id="recurstartdate" label="Start Date" v-model="availability.startDate"
                            :readOnly="true" placeholder="Select start date" />
                    </div>
                    <div class="col-md-6 mb-3">
                        <MaterialDatePicker id="recurenddate" label="End Date" v-model="availability.endDate"
                            :readOnly="true" :disabled="availability.indefinite" placeholder="Select end date" />
                        <div class="d-flex justify-content-end mt-2">
                            <MaterialSwitch id="indefinite" label="No end date (indefinite)" :checked="availability.indefinite"
                                @change="(e) => (availability.indefinite = e.target.checked)" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Specific date range -->
            <div v-else class="row">
                <div class="col-md-6 mb-3">
                    <MaterialDatePicker id="specificfromdate" label="From Date" v-model="availability.startDate"
                        :readOnly="true" placeholder="Select from date" />
                </div>
                <div class="col-md-6 mb-3">
                    <MaterialDatePicker id="specifictodate" label="To Date" v-model="availability.endDate"
                        :readOnly="true" placeholder="Select to date" />
                </div>
            </div>
        </div>

        <!-- ═══ Step 3: Apply To ═══ -->
        <div class="avail-section">
            <div class="avail-section__header">
                <span class="avail-step-badge">3</span>
                <div>
                    <h6 class="avail-section__title">Apply To <span class="badge bg-secondary ms-1" style="font-size:0.65rem;">Optional</span></h6>
                    <p class="avail-section__hint">Leave as "All Eligible Workgroups" to apply everywhere, or choose a specific skill, workgroup, or activity.</p>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 mb-3">
                    <MaterialSelect id="applyToEntity" label="Apply To Entity" v-model="availability.applyToEntityType"
                        :options="entityTypeOptions" @change="onApplyToEntityTypeChange" />
                </div>
                <div class="col-md-6 mb-3">
                    <MaterialSelect id="applyToEntityId" label="Apply To" v-model="availability.applyToEntityId"
                        :options="entityOptions" :disabled="entityOptions.length === 0" />
                    <small v-if="availability.applyToEntityType === 'all'" class="text-muted">
                        <i class="material-icons" style="font-size:14px;vertical-align:middle;">info</i>
                        Applies to all your eligible workgroups.
                    </small>
                    <small v-else-if="entityOptions.length === 0 && availability.applyToEntityType" class="text-warning">
                        <i class="material-icons" style="font-size:14px;vertical-align:middle;">warning</i>
                        No matching items found for this type.
                    </small>
                </div>
            </div>
        </div>

        <!-- ═══ Step 4: Details ═══ -->
        <div class="avail-section">
            <div class="avail-section__header">
                <span class="avail-step-badge">4</span>
                <div>
                    <h6 class="avail-section__title">Name &amp; Description <span class="badge bg-secondary ms-1" style="font-size:0.65rem;">Optional</span></h6>
                    <p class="avail-section__hint">Give this availability rule a friendly name so you can find it later.</p>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 mb-3">
                    <material-input id="name" type="text" label="Name" v-model="availability.name" />
                </div>
                <div class="col-md-6 mb-3">
                    <material-input id="description" type="text" label="Description" v-model="availability.description" />
                </div>
            </div>
        </div>

    </form>
</template>

<script setup>
import { computed, onMounted, ref, watch, inject } from "vue";
// Accept details prop for edit mode and optional personId when opened from volunteer page (no route userid)
const props = defineProps({
    details: {
        type: Object,
        default: null
    },
    personId: {
        type: [Number, String],
        default: null
    }
});
import MaterialInput from '@/components/common/MaterialInput.vue';
import MaterialSelect from '@/components/common/MaterialSelect.vue';
import MaterialSwitch from '@/components/common/MaterialSwitch.vue';
import MaterialMultiSelect from "@/components/common/MaterialMultiSelect.vue";
import MaterialDatePicker from '@/components/common/MaterialDatePicker.vue';
import store from "../../../store";
import { useRoute } from "vue-router";
import { getLocationId } from "../../../utils/SessionDataHandler";

// reactive flag for advanced options
const showAdvancedOption = ref(false);
const route = useRoute();

// Use personId from prop (volunteer page), inject (modal), or route params (user edit page)
const _injectedPersonId = inject('editUserPersonId', null);
const personId = computed(() => {
  if (props.personId != null && props.personId !== "") return Number(props.personId);
  if (_injectedPersonId?.value) return Number(_injectedPersonId.value);
  const fromRoute = Number(route.params.userid);
  return Number.isNaN(fromRoute) ? null : fromRoute;
});
const locationId = getLocationId();
const injectedPeopleGroupId = inject('editUserPeopleGroupId', null);
const getPeopleGroupId = () => {
    const value = injectedPeopleGroupId?.value ?? null;
    return value ? Number(value) : null;
};

// Generate default name and description (reactive to personId)
const defaultName = computed(() => `personid_${personId.value ?? "unknown"}_availability`);
const defaultDescription = computed(() => `personid_${personId.value ?? "unknown"}_availability`);

// Initial default state factory
const getInitialState = () => ({
    availabilityid: "",
    recurrenceid: "",
    name: defaultName.value,
    description: defaultDescription.value,
    type: "unavailable",
    dateRange: "range",
    repeatEvery: 1,
    repeatEveryType: "",
    days: [],
    weeks: [],
    weekOfMonth: [],
    weekOfMonth2: [],
    selectedDays: [],
    months: [],
    weeksOfYear: [],
    startDate: "",
    endDate: "",
    specificfromdate: "",
    specifictodate: "",
    indefinite: false,
    applyToEntityId: "",
    applyToEntityType: "all",
    _entities: {}
});
const availability = ref(getInitialState());

// Helper: check if a value looks like a numeric string (e.g., "1", "2")
function is_numeric_str(val) {
    if (val === null || val === undefined || val === '') return false;
    return !isNaN(Number(val)) && String(val).trim() !== '';
}

// Helper: convert plural period words to singular for the form dropdown
// DB stores "days"/"weeks"/"months"/"years"; form uses "day"/"week"/"month"/"year"
function toSingularPeriod(val) {
    if (!val) return '';
    const str = String(val).toLowerCase().trim();
    const map = {
        'days': 'day', 'day': 'day', 'daily': 'day',
        'weeks': 'week', 'week': 'week', 'weekly': 'week',
        'months': 'month', 'month': 'month', 'monthly': 'month',
        'years': 'year', 'year': 'year', 'yearly': 'year',
    };
    return map[str] || str;
}

// small note model used by MaterialMultiSelect text field
const note = ref("");
const entityTypeOptions = ref([]);
const entityOptions = ref([]);
// map of id -> full type object (from API) for easy lookup when saving
const entityTypeMap = ref({});
// User's assigned skill IDs — used to filter entity list when "Skill" type is selected
const userSkillIds = ref(new Set());

const emit = defineEmits(["saved", "save-error", "error"]);

const availabilityOptions = [
    { label: "Unavailable", value: "unavailable", color: "danger" },
    { label: "Only Available", value: "available", color: "success" },
];

// Computed class for availability type styling
const availabilityTypeClass = computed(() => {
    return availability.value.type === "available" ? "availability-available" : "availability-unavailable";
});

const dateRangeOptions = [
    { label: "Specific Date Range", value: "range" },
    { label: "Recurring Days", value: "recurring" },
];

const weekDays = [
    { label: "Mon", value: "1" },
    { label: "Tue", value: "2" },
    { label: "Wed", value: "3" },
    { label: "Thu", value: "4" },
    { label: "Fri", value: "5" },
    { label: "Sat", value: "6" },
    { label: "Sun", value: "7" },
];

const weekOnMonth = [
    { label: "First", value: "1" },
    { label: "Second", value: "2" },
    { label: "Third", value: "3" },
    { label: "Fourth", value: "4" },
    { label: "Fifth", value: "5" }
];

const weekOfMonth2 = [
    { label: "Week 1", value: "1" },
    { label: "Week 2", value: "2" },
    { label: "Week 3", value: "3" },
    { label: "Week 4", value: "4" },
    { label: "Week 5", value: "5" },
    { label: "Week 6", value: "6" },
];

const repeatEveryTypeOptions = [
    { label: "Day(s)", value: "day" },
    { label: "Week(s)", value: "week" },
    { label: "Month(s)", value: "month" },
    { label: "Year(s)", value: "year" },
];

const daysOfMonth = Array.from({ length: 31 }, (_, i) => {
    const n = i + 1;
    const suffix =
        n % 100 >= 11 && n % 100 <= 13
            ? "th"
            : n % 10 === 1
                ? "st"
                : n % 10 === 2
                    ? "nd"
                    : n % 10 === 3
                        ? "rd"
                        : "th";
    return { value: n, label: `${n}${suffix}` };
});

const weeksOfYear = ({ weeks = 52, labelPrefix = "Week " } = {}) =>
    Array.from({ length: weeks }, (_, i) => {
        const n = i + 1;
        // const valueNumber = pad ? String(n).padStart(2, "0") : String(n);
        return {
            label: `${labelPrefix}${n}`,
            value: n,
        };
    });

const months = [
    { label: "January", value: "1" },
    { label: "February", value: "2" },
    { label: "March", value: "3" },
    { label: "April", value: "4" },
    { label: "May", value: "5" },
    { label: "June", value: "6" },
    { label: "July", value: "7" },
    { label: "August", value: "8" },
    { label: "September", value: "9" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];

function toggleDay(day) {
    if (availability.value.days.includes(day)) {
        availability.value.days = availability.value.days.filter((d) => d !== day);
    } else {
        availability.value.days.push(day);
    }
}

function toggleWeeks(week) {
    if (availability.value.weeks.includes(week)) {
        availability.value.weeks = availability.value.weeks.filter((d) => d !== week);
    } else {
        availability.value.weeks.push(week);
    }
}

function toggleWeeksOfMonth(week) {
    if (availability.value.weekOfMonth.includes(week)) {
        availability.value.weekOfMonth = availability.value.weekOfMonth.filter((d) => d !== week);
    } else {
        availability.value.weekOfMonth.push(week);
    }
}

async function saveAvailability() {
    if (personId.value == null) {
        const err = new Error("Person ID is required. Open availability from the volunteer menu or a user profile.");
        emit("save-error", err);
        throw err;
    }
    // Extract IDs from multiple possible sources
    let availabilityId = availability.value.availabilityid || "";
    let recurrenceId = availability.value.recurrenceid || "";
    
    // If IDs are empty, try to get them from props.details directly
    if (props.details) {
        if (!availabilityId) {
            availabilityId = props.details.availabilityid || props.details.availabilityId || props.details.availability_id || props.details.id || "";
        }
        if (!recurrenceId) {
            recurrenceId = props.details.recurrenceid || props.details.recurrenceId || props.details.recurrence_id || "";
        }
    }
    
    // Convert to string and ensure they're not empty strings
    const hasAvailabilityId = availabilityId && String(availabilityId).trim() !== "";
    const hasRecurrenceId = recurrenceId && String(recurrenceId).trim() !== "";
    
    // If props.details exists, we're in edit mode
    const isEditMode = !!props.details;
    
    // Validate: In edit mode, API requires both IDs
    if (isEditMode) {
        if (!hasAvailabilityId || !hasRecurrenceId) {
            const missingIds = [];
            if (!hasAvailabilityId) missingIds.push("Availability ID");
            if (!hasRecurrenceId) missingIds.push("Recurrence ID");
            const errorMsg = `${missingIds.join(" and ")} ${missingIds.length > 1 ? "are" : "is"} required for editing. Please close and reopen the form, then try again.`;
            console.error("Edit mode requires both IDs:", { 
                availabilityId, 
                hasAvailabilityId,
                recurrenceId, 
                hasRecurrenceId,
                propsDetails: props.details 
            });
            emit("save-error", new Error(errorMsg));
            throw new Error(errorMsg);
        }
    }

    const payload = {
        personId: personId.value,
        locationId,
        peopleGroupId: getPeopleGroupId(),
        available: availability.value.type === "available",
        dateRange: availability.value.dateRange,
        applyToEntityId: availability.value.applyToEntityId,
        applyToEntityType: (function () {
            const sel = availability.value.applyToEntityType;
            const typeObj = entityTypeMap.value[sel];
            return typeObj ? typeObj.value : sel;
        })(),
        recurrencePeriod: availability.value.repeatEveryType || "weekly",
        frequency: availability.value.repeatEvery,
        daysOfWeek: availability.value.days.map((d) => parseInt(d)),
        recurStartDate: availability.value.dateRange === "recurring" ? availability.value.startDate : (availability.value.dateRange === "range" ? (availability.value.specificfromdate || availability.value.startDate) : null),
        recurEndDate: availability.value.dateRange === "recurring" ? (availability.value.indefinite ? null : availability.value.endDate) : (availability.value.dateRange === "range" ? (availability.value.specifictodate || availability.value.endDate) : null),
        specificfromdate: availability.value.dateRange === "range" ? (availability.value.specificfromdate || availability.value.startDate) : null,
        specifictodate: availability.value.dateRange === "range" ? (availability.value.specifictodate || availability.value.endDate) : null,
        recurrenceName: availability.value.name,
        recurrenceDescription: availability.value.description,
        daysofmonth: availability.value.selectedDays.map((d) => parseInt(d.value)),
        weeksofyear: availability.value.weeksOfYear.map((w) => parseInt(w.value)),
        months: availability.value.months.map((m) => parseInt(m.value)),
        weekdaysofmonth: availability.value.weekOfMonth.map((w) => parseInt(w)),
        weeksofmonth: availability.value.weeks.map((w) => parseInt(w)),
    };

    // Always include IDs in edit mode (API requires them)
    if (isEditMode) {
        if (hasAvailabilityId) {
            payload.availabilityid = String(availabilityId).trim();
        }
        if (hasRecurrenceId) {
            payload.recurrenceid = String(recurrenceId).trim();
        }
    }

    try {
        let res;
        console.log("props.details", props.details);
        console.log("Saving availability - isEditMode:", isEditMode, "payload:", payload);
        
        if (isEditMode) {
            // Edit mode
            res = await store.dispatch("editAvailability", payload);
        } else {
            // Add mode
            res = await store.dispatch("addAvailability", payload);
        }
        console.log("Saved successfully:", res);

        // Update IDs from response if they're returned (for future edits)
        if (res && !isEditMode) {
            const responseData = res.data || res;
            if (responseData.availabilityid || responseData.availabilityId) {
                availability.value.availabilityid = responseData.availabilityid || responseData.availabilityId || "";
            }
            if (responseData.recurrenceid || responseData.recurrenceId) {
                availability.value.recurrenceid = responseData.recurrenceid || responseData.recurrenceId || "";
            }
        }

        emit("saved", res);
    } catch (err) {
        console.error("Save availability error:", err);
        
        // If error is about missing IDs, clear the form state to force refresh
        const errorMessage = err?.message || err?.response?.data?.error || String(err);
        if (errorMessage.includes("Availability ID") || errorMessage.includes("Recurrence ID")) {
            console.warn("IDs missing error detected, resetting form state");
            // Reset IDs to force re-extraction on next save
            availability.value.availabilityid = "";
            availability.value.recurrenceid = "";
        }
        
        emit("save-error", err);
        throw err; // Re-throw so parent component can handle it
    }
}

// Reset form to initial state
function resetForm() {
    availability.value = getInitialState();
    showAdvancedOption.value = false;
    note.value = "";
    entityOptions.value = [];

    // Re-load entity types to restore dropdown state
    loadEntityTypes();
}

// Handle change for Apply To Entity dropdown
function onApplyToEntityTypeChange() {
    const selectedTypeId = availability.value.applyToEntityType;
    const entities = availability.value._entities || {};
    updateEntityOptions(selectedTypeId, entities);
}

// Expose methods to parent component
defineExpose({ saveAvailability, resetForm });

onMounted(async () => {
    await loadUserSkills();
    await loadEntityTypes();

    // Watch for details prop to be set after mount
    watch(() => props.details, (d) => {
        if (d) {
            // Handle different casing for IDs from API and nested structures
            // Try multiple possible paths: direct properties, nested data, etc.
            const availabilityId = d.availabilityid || d.availabilityId || d.availability_id || 
                                  d.id || d.data?.availabilityid || d.data?.availabilityId || 
                                  d.data?.availability_id || d.data?.id || "";
            const recurrenceId = d.recurrenceid || d.recurrenceId || d.recurrence_id || 
                                d.data?.recurrenceid || d.data?.recurrenceId || 
                                d.data?.recurrence_id || "";

            console.log("Loading availability details:", { d, availabilityId, recurrenceId });

            availability.value = {
                ...getInitialState(),
                availabilityid: availabilityId,
                recurrenceid: recurrenceId,
                name: d.recurrencename || d.name || "",
                description: d.recurrencedescription || d.description || "",
                type: d.available === false ? "unavailable" : "available",
                dateRange: d.dateRange || "range",
                // DB convention: recurrenceperiod = numeric interval ("1"), frequency = period word ("days")
                // Form expects: repeatEvery = number, repeatEveryType = singular word ("day")
                repeatEvery: (is_numeric_str(d.recurrenceperiod) ? Number(d.recurrenceperiod) : null) || (typeof d.frequency === 'number' ? d.frequency : null) || 1,
                repeatEveryType: toSingularPeriod(is_numeric_str(d.recurrenceperiod) ? d.frequency : d.recurrenceperiod) || "week",
                days: (d.daysofweek || []).map(String),
                weeks: (d.weeksofmonth || []).map(String),
                weekOfMonth: (d.weekdaysofmonth || []).map(String),
                weekOfMonth2: [],
                selectedDays: (d.daysofmonth || []).map(v => ({ value: v, label: String(v) })),
                months: (d.months || []).map(v => ({ value: v, label: months.find(m => m.value == v.toString())?.label || String(v) })),
                weeksOfYear: (d.weeksofyear || []).map(v => ({ value: v, label: `Week ${v}` })),
                startDate: d.recurstartdate || d.startdate || "",
                endDate: d.recurenddate || d.enddate || "",
                specificfromdate: d.specificfromdate || d.startdate || "",
                specifictodate: d.specifictodate || d.enddate || "",
                indefinite: d.recurenddate === null || d.indefinite || false,
                applyToEntityId: d.applytoentityid || d.applyToEntityId || "",
                applyToEntityType: d.applytoentitytype || d.applyToEntityType || "",
                _entities: availability.value._entities || {},
            };
            // Optionally, update entity dropdowns
            if (availability.value.applyToEntityType) {
                updateEntityOptions(availability.value.applyToEntityType);
            }
        }
    }, { immediate: true });
});

// Fetch the user's assigned skills so we can filter the skill entity list
async function loadUserSkills() {
    if (!personId.value) return;
    try {
        const res = await store.dispatch('fetchPersonSkillsModal', {
            personId: personId.value,
            locationId,
            peopleGroupId: getPeopleGroupId(),
        });
        const current = res?.current_skills || [];
        userSkillIds.value = new Set(current.map(s => Number(s.skillid || s.id)));
    } catch (err) {
        console.warn('Could not load user skills for filtering:', err);
    }
}

// Load entity types & entities from the store and populate select options
async function loadEntityTypes() {
    try {
        const res = await store.dispatch('fetchAvailabilityEntities', {
            personId: personId.value,
            locationId,
            peopleGroupId: getPeopleGroupId(),
        });
        const types = res?.entity_types || [];
        const entities = res?.entities || {};

        const map = {};
        const options = types.map((t) => {
            map[t.id] = t;
            return { label: t.label, value: t.id, description: t.description };
        });

        // Prepend "All Eligible Workgroups" as the first option
        entityTypeOptions.value = [
            { label: "All Eligible Workgroups", value: "all" },
            ...options
        ];
        entityTypeMap.value = map;
        availability.value._entities = entities;

        // Set default to "all" if not already set (for new forms)
        if (!availability.value.applyToEntityType) {
            availability.value.applyToEntityType = "all";
        }

        if (availability.value.applyToEntityType) {
            updateEntityOptions(availability.value.applyToEntityType, entities);
        }
    } catch (err) {
        console.error('Failed to load availability entities', err);
        emit("error", err);
    }
}

// Given a selected type id, populate entityOptions from availability._entities
// When the selected type is a "skill" type, filter to only the user's assigned skills.
function updateEntityOptions(selectedTypeId, entities = null) {
    const allEntities = entities || availability.value._entities || {};
    entityOptions.value = [];

    if (!selectedTypeId || selectedTypeId === 'all') {
        entityOptions.value = [];
        availability.value.applyToEntityId = null;
        return;
    }

    const entries = allEntities[selectedTypeId];

    if (!entries) {
        entityOptions.value = [];
        availability.value.applyToEntityId = null;
        return;
    }

    let list = Array.isArray(entries) ? entries : [entries];

    // Filter to only the user's skills when the selected entity type is "Skill"
    const typeObj = entityTypeMap.value[selectedTypeId];
    const typeLabel = (typeObj?.label || typeObj?.value || selectedTypeId || '').toString().toLowerCase();
    if (typeLabel.includes('skill')) {
        list = list.filter(e => userSkillIds.value.has(Number(e.id)));
    }

    entityOptions.value = list.map((e) => ({
        label: e.name + (e.activity_name ? ` - ${e.activity_name}` : ''),
        value: e.id
    }));

    if (!entityOptions.value.find((o) => o.value === availability.value.applyToEntityId)) {
        availability.value.applyToEntityId = null;
    }
}
</script>

<style scoped>
/* ── Form layout ── */
.avail-form {
    display: flex;
    flex-direction: column;
    gap: 0;
}

/* ── Status Banner ── */
.avail-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 10px;
    margin-bottom: 18px;
    font-weight: 600;
    font-size: 0.9rem;
    transition: background 0.3s, color 0.3s;
}
.avail-banner--green {
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    color: #155724;
    border-left: 4px solid #28a745;
}
.avail-banner--red {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    color: #721c24;
    border-left: 4px solid #dc3545;
}
.avail-banner__icon {
    font-size: 28px;
}
.avail-banner__text {
    line-height: 1.3;
}

/* ── Section Cards ── */
.avail-section {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 18px 20px 10px;
    margin-bottom: 14px;
}
.avail-section__header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
}
.avail-section__title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: #344767;
}
.avail-section__hint {
    margin: 2px 0 0;
    font-size: 0.78rem;
    color: #8392ab;
    line-height: 1.3;
}

/* ── Step Badge ── */
.avail-step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    flex-shrink: 0;
}

/* ── Day Chip Buttons ── */
.avail-day-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.avail-day-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    padding: 6px 14px;
    border: 2px solid #dee2e6;
    border-radius: 20px;
    background: #fff;
    color: #495057;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
}
.avail-day-chip:hover {
    border-color: #667eea;
    color: #667eea;
    background: #f0f1ff;
}
.avail-day-chip--active {
    border-color: #667eea;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
}
.avail-day-chip--active:hover {
    background: linear-gradient(135deg, #5a6fe0, #6a42a0);
    color: #fff;
}
.avail-day-chip--sm {
    min-width: 44px;
    padding: 4px 10px;
    font-size: 0.75rem;
}

/* ── Advanced Toggle ── */
.avail-advanced-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    margin-bottom: 12px;
    border-radius: 6px;
    background: #f8f9fa;
    color: #667eea;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    user-select: none;
}
.avail-advanced-toggle:hover {
    background: #eef0ff;
}
.avail-advanced-panel {
    background: #fafbfc;
    border: 1px dashed #dee2e6;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 14px;
}

/* ── Select color overrides ── */
.availability-available :deep(select#availabilityType) {
    color: #28a745 !important;
    font-weight: 600;
}
.availability-unavailable :deep(select#availabilityType) {
    color: #dc3545 !important;
    font-weight: 600;
}
.availability-available :deep(select#availabilityType option),
.availability-unavailable :deep(select#availabilityType option) {
    color: #000 !important;
    background: #fff !important;
}
</style>
